import React, { useEffect, useState } from 'react';
import { Control, useController } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { StatusBanner } from '@xingternal/banner';
import { spaceXXL } from '@xingternal/tokens';

import { DocumentsUpload } from '@/domain/job-application/components';
import { useCvDocumentUploadProps } from '@/domain/job-application/contexts';
import { LIFormFieldValues, LOFormFieldValues } from '@/domain/job-application/types';

import { Section } from '@/domain/job-application/job-application.common.styles';

export const UploadDocsBannerWrapper = styled.div`
  margin: ${spaceXXL} 0;
`;

type Props = {
  control: Control<LOFormFieldValues | LIFormFieldValues>;
  isRequired?: boolean;
  updateValue: (value: string, field: 'cvDoc' | 'otherDocs') => void;
};

export const CvDocumentUploadField: React.VFC<Props> = ({ control, isRequired = false, updateValue }) => {
  const [showRequiredUploadError, setShowRequiredUploadError] = useState(false);
  const { uploads, ...docUploadProps } = useCvDocumentUploadProps();

  const {
    field: { ref: cvFieldRef, value: cvFieldValue },
    fieldState: { error: cvFieldError },
  } = useController({
    name: 'cvDoc',
    control,
    rules: { required: isRequired },
    defaultValue: '',
  });

  useEffect(() => {
    const updatedUploads = uploads
      .map(({ id }) => id)
      .filter(Boolean)
      .join(',');
    if (cvFieldValue !== updatedUploads) updateValue(updatedUploads, 'cvDoc');
  }, [uploads, cvFieldValue, updateValue]);

  useEffect(() => {
    if (cvFieldError?.type === 'required') {
      setShowRequiredUploadError(true);
    } else {
      setShowRequiredUploadError(false);
    }
  }, [cvFieldError]);

  return (
    <Section>
      <DocumentsUpload uploads={uploads} {...docUploadProps} buttonRef={cvFieldRef} trackingOrigin="cv" maxFiles={1} />
      {showRequiredUploadError && (
        <UploadDocsBannerWrapper>
          <StatusBanner display="inline" variant="error">
            <FormattedMessage id="JOBS_POSTING_APPLY_FORM_LO_DOCUMENTS_FIELD_ERROR" />
          </StatusBanner>
        </UploadDocsBannerWrapper>
      )}
    </Section>
  );
};
